<template>
  <div class="drawer-overlay" @click.self="close">
    <div class="drawer">
      <div class="drawer-topbar">
        <div class="drawer-topbar-crumb">
          <span class="crumb" @click="close">Properties</span>
          <svg viewBox="0 0 24 24"><path d="M9 18l6-6-6-6"/></svg>
          <span class="crumb-current">{{ property.name }}</span>
        </div>
        <button class="btn-close" @click="close" aria-label="Close">
          <svg viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
      </div>

      <div class="drawer-body">
        <div v-if="loading" class="loading-state"><div class="spinner"></div><p>Loading owner…</p></div>

        <template v-else>
          <!-- HERO -->
          <div class="prop-hero">
            <div class="prop-hero-left">
              <div class="prop-avatar-lg">{{ initials(property.name) }}</div>
              <div>
                <div class="prop-hero-name">{{ property.name }}</div>
                <div class="prop-hero-meta">{{ property.address || '—' }}<span v-if="addedDate"> · Added {{ addedDate }}</span></div>
              </div>
            </div>
            <div class="prop-hero-stats">
              <div><div class="prop-hero-stat-label">Commission</div><div class="prop-hero-stat-value">{{ commissionLabel }}</div></div>
              <div><div class="prop-hero-stat-label">Owner</div><div class="prop-hero-stat-value" :style="{color: ownerColor}">{{ ownerStatusLabel }}</div></div>
              <div><div class="prop-hero-stat-label">DAC7</div><div class="prop-hero-stat-value" :style="{color: dac7Color}">{{ dac7StatusLabel }}</div></div>
            </div>
          </div>

          <!-- TABS -->
          <div class="settings-tabs">
            <div class="settings-tab" :class="{active: tab==='owner'}" @click="tab='owner'">Owner &amp; compliance</div>
            <div class="settings-tab" :class="{active: tab==='financial'}" @click="onTabChange('financial')">Financial settings</div>
            <div class="settings-tab" :class="{active: tab==='details'}" @click="onTabChange('details')">Property details</div>
          </div>

          <!-- TAB: OWNER & COMPLIANCE -->
          <div v-if="tab==='owner'">
            <!-- Owner account block -->
            <div class="owner-assignment">
              <div class="owner-assignment-header">
                <div class="card-title">
                  <svg viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  Owner account
                </div>
                <span class="pill" :class="ownerPillClass">{{ ownerPillLabel }}</span>
              </div>

              <!-- Verified block (alternate state) -->
              <div v-if="isVerified" class="owner-verified-block">
                <div class="owner-avatar">{{ initials(form.full_legal_name) }}</div>
                <div style="flex:1;">
                  <div style="font-family:'Outfit',sans-serif;font-size:1rem;font-weight:700;color:var(--text);margin-bottom:3px;">{{ form.full_legal_name }}</div>
                  <div style="font-size:12px;color:var(--text-muted);display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
                    <span>{{ form.primary_email }}</span><span style="color:var(--border);">·</span>
                    <span>{{ ownershipTypeLabel }}</span><span style="color:var(--border);">·</span>
                    <span>{{ countryLabel }}</span>
                  </div>
                </div>
                <div style="display:flex;gap:8px;align-items:center;">
                  <span class="pill pill-green">Verified ✓</span>
                  <button class="btn btn-outline btn-sm future" disabled title="Coming soon">View their form <span class="future-tag">future</span></button>
                </div>
              </div>

              <!-- Invite block (default) -->
              <div v-else style="padding:20px 22px;">
                <div style="display:flex;align-items:flex-start;gap:12px;margin-bottom:16px;">
                  <div class="owner-mini-avatar">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  </div>
                  <div style="flex:1;">
                    <div style="font-weight:600;font-size:13px;color:var(--text);margin-bottom:2px;">{{ form.full_legal_name || 'No name yet' }}</div>
                    <div style="font-size:12px;color:var(--text-muted);">{{ form.primary_email || 'No email' }}</div>
                  </div>
                  <span class="pill" :class="linkPillClass">{{ linkPillLabel }}</span>
                </div>
                <div class="invite-row">
                  <div style="flex:1;">
                    <label class="field-label" style="display:block;margin-bottom:5px;">Owner email</label>
                    <input type="email" v-model="form.primary_email" placeholder="owner@email.com"/>
                  </div>
                  <button class="btn btn-primary"
                          :disabled="!canSendSetupLink || sendingLink || !exists"
                          @click="sendSetupLink"
                          style="flex-shrink:0;height:38px;">
                    <svg viewBox="0 0 24 24"><path d="M22 2L11 13"/><path d="M22 2L15 22 11 13 2 9l20-7z"/></svg>
                    {{ sendingLink
                        ? 'Sending…'
                        : (linkStatus.state === 'pending' ? 'Resend setup link' : 'Send setup link') }}
                  </button>
                </div>
                <div class="invite-status-row"
                     :class="{ pending: linkStatus.state !== 'pending' && linkStatus.state !== 'used', ok: linkStatus.state === 'pending' }"
                     style="margin-top:10px;">
                  <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
                  <span>{{ linkStatusMessage }}</span>
                </div>
                <div v-if="linkSendError" class="invite-status-row error" style="margin-top:8px;">
                  <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><circle cx="12" cy="16" r="0.5" fill="currentColor"/></svg>
                  <span>{{ linkSendError }}</span>
                </div>
                <div v-if="!exists" class="invite-status-row pending" style="margin-top:6px;">
                  <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4"/></svg>
                  <span>Save the owner first (bottom of the drawer) before sending a link.</span>
                </div>
              </div>
            </div>

            <!-- Owner details card -->
            <div class="card">
              <div class="card-header">
                <div>
                  <div class="card-title">
                    <svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg>
                    Owner details
                  </div>
                  <div class="card-subtitle">Required to process monthly payments</div>
                </div>
                <span class="pill" :class="ownerDetailsPillClass">{{ ownerDetailsPillLabel }}</span>
              </div>
              <div class="card-body" style="padding:6px 22px;">
                <ComplianceItem
                  label="Name"
                  :value="form.full_legal_name"
                  :editing="editingField === 'full_legal_name'"
                  @edit="editingField = 'full_legal_name'"
                  @save="onFieldSave"
                  @cancel="editingField = null"
                  field-name="full_legal_name"
                  :form="form"
                />
                <ComplianceItem
                  label="Email address"
                  :value="form.primary_email"
                  :editing="editingField === 'primary_email'"
                  @edit="editingField = 'primary_email'"
                  @save="onFieldSave"
                  @cancel="editingField = null"
                  field-name="primary_email"
                  input-type="email"
                  :form="form"
                />
                <ComplianceItem
                  label="Phone number"
                  :value="form.phone_number"
                  :editing="editingField === 'phone_number'"
                  @edit="editingField = 'phone_number'"
                  @save="onFieldSave"
                  @cancel="editingField = null"
                  field-name="phone_number"
                  input-type="tel"
                  :form="form"
                />
                <ComplianceItem
                  label="Bank account details"
                  fallback="Needed to process monthly settlements"
                  :value="''"
                  future
                />
              </div>
            </div>

            <!-- DAC7 compliance details card -->
            <div class="card">
              <div class="card-header">
                <div>
                  <div class="card-title" style="color:var(--gold);">
                    <svg viewBox="0 0 24 24" style="stroke:var(--gold);"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                    DAC7 compliance details
                  </div>
                  <div class="card-subtitle">Additional details required for annual platform reporting</div>
                </div>
                <span class="pill" :class="dac7DetailsPillClass">{{ dac7DetailsPillLabel }}</span>
              </div>
              <div class="card-body" style="padding:6px 22px;">
                <ComplianceItem
                  label="Home address"
                  :value="addressDisplay"
                  fallback="Awaiting owner"
                  :editing="editingField === 'address'"
                  @edit="editingField = 'address'"
                  @save="onFieldSave"
                  @cancel="editingField = null"
                  field-name="address"
                  input-type="address-group"
                  :form="form"
                />
                <ComplianceItem
                  label="Date of birth"
                  :value="dobDisplay"
                  fallback="Awaiting owner"
                  :editing="editingField === 'date_of_birth'"
                  @edit="editingField = 'date_of_birth'"
                  @save="onFieldSave"
                  @cancel="editingField = null"
                  field-name="date_of_birth"
                  input-type="date"
                  :form="form"
                />
                <ComplianceItem
                  label="Ownership type"
                  :value="ownershipTypeLabel !== '—' ? ownershipTypeLabel : ''"
                  fallback="Awaiting owner"
                  :editing="editingField === 'ownership_type'"
                  @edit="editingField = 'ownership_type'"
                  @save="onFieldSave"
                  @cancel="editingField = null"
                  field-name="ownership_type"
                  input-type="select-ownership"
                  :form="form"
                />
                <ComplianceItem
                  label="Country of tax residence"
                  :value="countryLabel"
                  fallback="Awaiting owner"
                  :editing="editingField === 'country'"
                  @edit="editingField = 'country'"
                  @save="onFieldSave"
                  @cancel="editingField = null"
                  field-name="country"
                  input-type="select-country"
                  :form="form"
                />
                <ComplianceItem
                  label="Tax reference (UTR / NI)"
                  :value="taxRefDisplay"
                  fallback="Awaiting owner"
                  :editing="editingField === 'tax_ref'"
                  @edit="editingField = 'tax_ref'"
                  @save="onFieldSave"
                  @cancel="editingField = null"
                  field-name="tax_ref"
                  input-type="tax-group"
                  :form="form"
                />
                <ComplianceItem
                  label="Identity document"
                  :value="identityDisplay"
                  fallback="Passport or driving licence"
                  :editing="editingField === 'identity_document'"
                  @edit="editingField = 'identity_document'"
                  @save="onFieldSave"
                  @cancel="editingField = null"
                  field-name="identity_document"
                  input-type="file"
                  :form="form"
                  @file-change="onFileChange"
                />
              </div>
            </div>

            <!-- DAC7 obligation card -->
            <div class="card">
              <div class="card-header">
                <div>
                  <div class="card-title" style="color:var(--gold);">
                    <svg viewBox="0 0 24 24" style="stroke:var(--gold);"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/></svg>
                    DAC7 reporting obligation
                  </div>
                  <div class="card-subtitle">UK rules · in force from 1 January 2024</div>
                </div>
              </div>
              <div class="card-body">
                <div class="info-box">
                  <strong>All owners must be reported — there is no minimum earnings threshold under UK rules.</strong> Unlike the EU's DAC7 (which has a €2,000 / 30 transaction de minimis), the UK's equivalent rules require platforms to collect and report information on every seller regardless of how much they earn. Owner details must be complete and submitted to HMRC by 31 January each year.
                </div>
                <div class="oblig-grid">
                  <div class="oblig-cell">
                    <div class="oblig-label">Annual reporting deadline</div>
                    <div class="oblig-value">31 January</div>
                    <div class="oblig-sub">Each year — covering prior calendar year</div>
                  </div>
                  <div class="oblig-cell red">
                    <div class="oblig-label">Penalty for non-compliance</div>
                    <div class="oblig-value" style="color:var(--red);">£5,000</div>
                    <div class="oblig-sub">Per HMRC notice issued</div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="error" class="error-box">{{ error }}</div>
          </div>

          <!-- TAB: FINANCIAL SETTINGS -->
          <div v-else-if="tab==='financial'">
            <div class="info-box">
              <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
              OTA commission rates are inherited from Settings. Override below only if this property has a different arrangement.
            </div>

            <div v-if="settingsLoading" class="loading-state"><div class="spinner"></div><p>Loading settings…</p></div>

            <template v-else>
              <div class="card">
                <div class="card-header">
                  <div class="card-title">
                    <svg viewBox="0 0 24 24"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
                    Management fee
                  </div>
                </div>
                <div class="card-body">
                  <div class="field-grid">
                    <div class="field">
                      <label class="field-label">Fee rate</label>
                      <div style="display:flex;align-items:center;gap:8px;">
                        <input type="number" v-model.number="settings.management_fee_rate" style="width:80px;"/>
                        <span style="font-size:13px;color:var(--text-muted);">%</span>
                      </div>
                    </div>
                    <div class="field">
                      <label class="field-label">Calculation base</label>
                      <select v-model="settings.management_fee_base">
                        <option value="net">Net income (after platform fees)</option>
                        <option value="revenue">Total revenue</option>
                        <option value="gross">Gross profit — %</option>
                        <option value="grossFixed">Gross profit — fixed £</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div class="card">
                <div class="card-header">
                  <div>
                    <div class="card-title">
                      <svg viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/></svg>
                      Cleaning
                    </div>
                    <div class="card-subtitle">Separate from laundry even if the same company invoices both. Toggle off if this property doesn't charge for cleaning — the row is skipped on the owner statement.</div>
                  </div>
                  <div style="display:flex;gap:8px;align-items:center;">
                    <div :class="settings.cleaning_vat_applicable ? 'vat-badge on' : 'vat-badge off'">{{ settings.cleaning_vat_applicable ? 'VAT applied' : 'No VAT' }}</div>
                    <label class="toggle"><input type="checkbox" v-model="settings.cleaning_enabled"/><div class="toggle-track"></div><div class="toggle-thumb"></div></label>
                  </div>
                </div>
                <div v-if="settings.cleaning_enabled" class="card-body">
                  <div class="radio-group" style="margin-bottom:14px;">
                    <div class="radio-opt" :class="{active: settings.cleaning_mode === 'rentlio'}" @click="settings.cleaning_mode = 'rentlio'"><div class="radio-dot-wrap"></div><div class="radio-opt-text"><strong>Use Rentlio data</strong><span>Pull fee directly from channel manager per booking</span></div></div>
                    <div class="radio-opt" :class="{active: settings.cleaning_mode === 'monthly'}" @click="settings.cleaning_mode = 'monthly'"><div class="radio-dot-wrap"></div><div class="radio-opt-text"><strong>Fixed monthly amount</strong><span>Same cost every month regardless of bookings</span></div></div>
                    <div class="radio-opt" :class="{active: settings.cleaning_mode === 'fixed'}" @click="settings.cleaning_mode = 'fixed'"><div class="radio-dot-wrap"></div><div class="radio-opt-text"><strong>Fixed per booking</strong><span>Fixed cost × number of bookings this month</span></div></div>
                    <div class="radio-opt" :class="{active: settings.cleaning_mode === 'per_booking'}" @click="settings.cleaning_mode = 'per_booking'"><div class="radio-dot-wrap"></div><div class="radio-opt-text"><strong>Per booking</strong><span>Cost × number of bookings this month</span></div></div>
                  </div>
                  <div v-if="settings.cleaning_mode === 'monthly'" style="display:flex;gap:10px;align-items:flex-end;margin-bottom:12px;">
                    <div class="field"><label class="field-label">Monthly amount</label><div class="input-prefix"><span>£</span><input type="number" v-model.number="settings.cleaning_fee"/></div></div>
                    <div :class="cleaningTotalPillClass">{{ cleaningTotalLabel }}</div>
                  </div>
                  <div v-else-if="settings.cleaning_mode === 'fixed' || settings.cleaning_mode === 'per_booking'" style="display:flex;gap:10px;align-items:flex-end;margin-bottom:12px;">
                    <div class="field"><label class="field-label">Cost per booking</label><div class="input-prefix"><span>£</span><input type="number" v-model.number="settings.cleaning_per_booking"/></div></div>
                    <div :class="cleaningTotalPillClass">{{ cleaningTotalLabel }}</div>
                  </div>
                  <div class="toggle-row">
                    <div class="toggle-info">
                      <strong>Apply VAT on cleaning</strong>
                      <span>Turn on if your company supplies the cleaning as a service rather than passing through a third-party cost</span>
                    </div>
                    <label class="toggle"><input type="checkbox" v-model="settings.cleaning_vat_applicable"/><div class="toggle-track"></div><div class="toggle-thumb"></div></label>
                  </div>
                </div>
              </div>

              <div class="card">
                <div class="card-header">
                  <div>
                    <div class="card-title">
                      <svg viewBox="0 0 24 24"><path d="M3 6h18M3 12h18M3 18h18"/></svg>
                      Laundry &amp; linen supply
                    </div>
                    <div class="card-subtitle">Separate line item on the report even if invoiced with cleaning. Toggle off if this property doesn't use a laundry service — the row is skipped on the owner statement.</div>
                  </div>
                  <div style="display:flex;gap:8px;align-items:center;">
                    <div :class="settings.laundry_vat_applicable ? 'vat-badge on' : 'vat-badge off'">{{ settings.laundry_vat_applicable ? 'VAT applied' : 'No VAT' }}</div>
                    <label class="toggle"><input type="checkbox" v-model="settings.laundry_enabled"/><div class="toggle-track"></div><div class="toggle-thumb"></div></label>
                  </div>
                </div>
                <div v-if="settings.laundry_enabled" class="card-body">
                  <div class="radio-group" style="margin-bottom:14px;">
                    <div class="radio-opt" :class="{active: settings.laundry_calculation_mode === 'fixed'}" @click="settings.laundry_calculation_mode = 'fixed'"><div class="radio-dot-wrap"></div><div class="radio-opt-text"><strong>Fixed monthly amount</strong><span>Same cost every month regardless of bookings</span></div></div>
                    <div class="radio-opt" :class="{active: settings.laundry_calculation_mode === 'per_booking'}" @click="settings.laundry_calculation_mode = 'per_booking'"><div class="radio-dot-wrap"></div><div class="radio-opt-text"><strong>Per booking</strong><span>Cost × number of bookings this month</span></div></div>
                  </div>
                  <div v-if="settings.laundry_calculation_mode === 'fixed'" style="display:flex;gap:10px;align-items:flex-end;margin-bottom:12px;">
                    <div class="field"><label class="field-label">Fixed monthly amount</label><div class="input-prefix"><span>£</span><input type="number" v-model.number="settings.laundry_fee"/></div></div>
                    <div :class="laundryTotalPillClass">{{ laundryTotalLabel }}</div>
                  </div>
                  <div v-else style="display:flex;gap:10px;align-items:flex-end;margin-bottom:12px;">
                    <div class="field"><label class="field-label">Cost per booking</label><div class="input-prefix"><span>£</span><input type="number" v-model.number="settings.laundry_per_booking"/></div></div>
                    <div :class="laundryTotalPillClass">{{ laundryTotalLabel }}</div>
                  </div>
                  <div class="toggle-row">
                    <div class="toggle-info">
                      <strong>Apply VAT on laundry</strong>
                      <span>Turn off if passing through a third-party laundry cost as a disbursement rather than supplying it yourself</span>
                    </div>
                    <label class="toggle"><input type="checkbox" v-model="settings.laundry_vat_applicable"/><div class="toggle-track"></div><div class="toggle-thumb"></div></label>
                  </div>
                </div>
              </div>

              <div class="card">
                <div class="card-header">
                  <div>
                    <div class="card-title">
                      <svg viewBox="0 0 24 24"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>
                      Consumables
                    </div>
                    <div class="card-subtitle">Optional — only set if you charge a standing monthly amount</div>
                  </div>
                  <label class="toggle"><input type="checkbox" v-model="settings.consumables_enabled"/><div class="toggle-track"></div><div class="toggle-thumb"></div></label>
                </div>
                <div v-if="settings.consumables_enabled" class="card-body">
                  <div style="display:flex;gap:10px;align-items:flex-end;margin-bottom:12px;">
                    <div class="field"><label class="field-label">Monthly amount</label><div class="input-prefix"><span>£</span><input type="number" v-model.number="settings.consumables"/></div></div>
                    <div class="vat-note"><svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg>VAT applied automatically</div>
                    <div class="consumables-pill">{{ consumablesTotalLabel }}</div>
                  </div>
                  <div class="info-note">
                    A fixed monthly service charge — VAT applies automatically as you are supplying this. Shown on the owner report as "Consumables &amp; supplies". For one-off or receipt-based consumable costs, add them as a line item directly in the report preview.
                  </div>
                </div>
              </div>

              <div class="card">
                <div class="card-header">
                  <div class="card-title">
                    <svg viewBox="0 0 24 24"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>
                    OTA commission overrides
                  </div>
                  <span style="font-size:11px;color:var(--text-muted);">Leave blank to use global Settings rates</span>
                </div>
                <div class="card-body">
                  <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:14px;">
                    <div class="field">
                      <label class="field-label">Airbnb override</label>
                      <div class="input-prefix"><span>%</span><input type="number" v-model="settings.airbnb_commission_rate" :placeholder="airbnbGlobalPlaceholder"/></div>
                    </div>
                    <div class="field">
                      <label class="field-label">Booking.com override</label>
                      <div class="input-prefix"><span>%</span><input type="number" v-model="settings.booking_com_commission_rate" :placeholder="bookingGlobalPlaceholder"/></div>
                    </div>
                    <div class="field">
                      <label class="field-label">Other platforms</label>
                      <div class="input-prefix"><span>%</span><input type="number" v-model="settings.other_platform_commission_rate" :placeholder="otherGlobalPlaceholder"/></div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- LOCAL TOURISM LEVY (Scottish Visitor Levy and equivalents) -->
              <div class="card">
                <div class="card-header">
                  <div>
                    <div class="card-title">
                      <svg viewBox="0 0 24 24"><path d="M3 21h18M5 21V10l7-6 7 6v11M9 9h1M14 9h1M9 14h1M14 14h1M9 19h6"/></svg>
                      Local tourism levy
                    </div>
                    <div class="card-subtitle">Configure any local visitor or tourism tax that applies to this property. Collected on behalf of the local authority, not owner revenue.</div>
                  </div>
                  <label class="toggle"><input type="checkbox" v-model="settings.visitor_levy_enabled"/><div class="toggle-track"></div><div class="toggle-thumb"></div></label>
                </div>
                <div v-if="settings.visitor_levy_enabled" class="card-body">
                  <label class="field-label" style="display:block;margin-bottom:6px;">Levy calculation</label>
                  <div style="display:flex;flex-direction:column;gap:8px;margin-bottom:14px;">
                    <label class="method-opt" :class="{active: settings.visitor_levy_method==='pct'}">
                      <input type="radio" value="pct" v-model="settings.visitor_levy_method"/>
                      <span>Percentage of accommodation revenue</span>
                    </label>
                    <label class="method-opt" :class="{active: settings.visitor_levy_method==='booking'}">
                      <input type="radio" value="booking" v-model="settings.visitor_levy_method"/>
                      <span>Fixed amount per booking</span>
                    </label>
                    <label class="method-opt" :class="{active: settings.visitor_levy_method==='night'}">
                      <input type="radio" value="night" v-model="settings.visitor_levy_method"/>
                      <span>Fixed amount per night</span>
                    </label>
                    <label class="method-opt" :class="{active: settings.visitor_levy_method==='guest'}">
                      <input type="radio" value="guest" v-model="settings.visitor_levy_method"/>
                      <span>Fixed amount per guest</span>
                    </label>
                  </div>
                  <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:12px;">
                    <div class="field">
                      <label class="field-label">{{ settings.visitor_levy_method === 'pct' ? 'Levy amount (%)' : 'Levy amount (£)' }}</label>
                      <div class="input-prefix">
                        <span>{{ settings.visitor_levy_method === 'pct' ? '%' : '£' }}</span>
                        <input type="number" step="0.01" v-model.number="settings.visitor_levy_rate"/>
                      </div>
                    </div>
                    <div class="field">
                      <label class="field-label">Maximum chargeable nights</label>
                      <input v-if="levyCapApplicable" type="number" min="0" v-model.number="settings.visitor_levy_night_cap" placeholder="5"/>
                      <input v-else type="text" value="Not applicable" disabled/>
                      <div v-if="levyCapApplicable" style="font-size:11px;color:var(--text-muted);margin-top:4px;">Leave blank if the levy applies for the entire stay.</div>
                    </div>
                  </div>
                  <div class="field" style="margin-bottom:12px;">
                    <label class="field-label">Statement description</label>
                    <input type="text" v-model="settings.visitor_levy_description" placeholder="e.g. Edinburgh Visitor Levy"/>
                  </div>
                  <div class="field" style="margin-bottom:12px;">
                    <label class="field-label">Scheme go-live date (optional)</label>
                    <input type="date" v-model="settings.visitor_levy_live_date"/>
                    <div style="font-size:11px;color:var(--text-muted);margin-top:4px;">Stays straddling this date only count nights on or after go-live.</div>
                  </div>
                  <div style="display:flex;flex-direction:column;gap:8px;margin-bottom:14px;">
                    <label style="display:flex;gap:8px;align-items:center;font-size:13px;">
                      <input type="checkbox" v-model="settings.visitor_levy_show_on_statement"/>
                      Show as a separate line on owner statements
                    </label>
                    <label style="display:flex;gap:8px;align-items:center;font-size:13px;">
                      <input type="checkbox" v-model="settings.visitor_levy_xero_liability"/>
                      Export to Xero as a liability (not owner income)
                    </label>
                    <label style="display:flex;gap:8px;align-items:center;font-size:13px;">
                      <input type="checkbox" v-model="settings.visitor_levy_bundled_extras_excluded"/>
                      Exclude breakfast and other bundled extras from the accommodation base
                    </label>
                  </div>
                  <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;padding-top:12px;border-top:1px solid var(--border);">
                    <div class="field">
                      <label class="field-label">VAT treatment</label>
                      <select v-model.number="settings.visitor_levy_vat_rate">
                        <option :value="20">20% · standard rate</option>
                        <option :value="0">0% · not VAT registered</option>
                      </select>
                      <div style="font-size:11px;color:var(--text-muted);margin-top:4px;">VAT is deemed to sit inside the amount collected from guests.</div>
                    </div>
                    <div class="field">
                      <label class="field-label">Provider retention (%)</label>
                      <div class="input-prefix">
                        <span>%</span>
                        <input type="number" step="0.01" min="0" max="100" v-model.number="settings.visitor_levy_provider_retention_pct"/>
                      </div>
                      <div style="font-size:11px;color:var(--text-muted);margin-top:4px;">Portion you keep as admin fee (e.g. Edinburgh 2%). Posts to a separate Xero income account.</div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="settingsError" class="error-box">{{ settingsError }}</div>

              <div class="btn-row-actions">
                <button class="btn btn-outline" @click="resetSettings">Reset</button>
                <button class="btn btn-primary" :disabled="savingSettings" @click="saveSettings">
                  <span v-if="savingSettings">Saving…</span>
                  <span v-else>Save financial settings</span>
                </button>
              </div>
            </template>
          </div>

          <!-- TAB: PROPERTY DETAILS -->
          <div v-else-if="tab==='details'">
            <template v-if="loadingDetails">
              <div class="loading-block">Loading details…</div>
            </template>
            <template v-else>
              <div class="card">
                <div class="card-header">
                  <div class="card-title">
                    <svg viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/></svg>
                    Property details
                  </div>
                </div>
                <div class="card-body">
                  <div class="field-grid">
                    <div class="field">
                      <label class="field-label">Property name</label>
                      <input type="text" :value="property.name" readonly/>
                    </div>
                    <div class="field">
                      <label class="field-label">Rentlio property ID</label>
                      <input type="text" :value="property.id" readonly/>
                    </div>
                    <div class="field span2">
                      <label class="field-label">Address line 1</label>
                      <input type="text" v-model="details.address_line_1" :placeholder="pmsFallbackPlaceholder('address_line_1', 'Address line 1')"/>
                    </div>
                    <div class="field span2">
                      <label class="field-label">Address line 2</label>
                      <input type="text" v-model="details.address_line_2" placeholder="Apartment, floor, building (optional)"/>
                    </div>
                    <div class="field">
                      <label class="field-label">Town / City</label>
                      <input type="text" v-model="details.city" :placeholder="pmsFallbackPlaceholder('city', 'City')"/>
                    </div>
                    <div class="field">
                      <label class="field-label">Postcode</label>
                      <input type="text" v-model="details.postal_code" :placeholder="pmsFallbackPlaceholder('postal_code', 'Postcode')"/>
                    </div>
                    <div class="field">
                      <label class="field-label">Country</label>
                      <select v-model="details.country">
                        <option value="">Select…</option>
                        <option value="GB">United Kingdom</option>
                        <option value="IE">Ireland</option>
                        <option value="FR">France</option>
                        <option value="ES">Spain</option>
                        <option value="IT">Italy</option>
                        <option value="PT">Portugal</option>
                        <option value="DE">Germany</option>
                        <option value="NL">Netherlands</option>
                        <option value="BE">Belgium</option>
                        <option value="US">United States</option>
                      </select>
                    </div>
                  </div>
                  <div class="info-note" style="margin-top:12px;">
                    Leave a field blank to fall back to the address supplied by the PMS.
                  </div>
                </div>
              </div>

              <div v-if="detailsError" class="error-box">{{ detailsError }}</div>

              <div class="btn-row-actions">
                <button class="btn btn-outline" @click="resetDetails">Reset</button>
                <button class="btn btn-primary" :disabled="savingDetails" @click="saveDetails">
                  <span v-if="savingDetails">Saving…</span>
                  <span v-else>Save property details</span>
                </button>
              </div>
            </template>
          </div>

          <!-- Owner tab actions inline -->
          <div v-if="tab==='owner'" class="btn-row-actions">
            <button class="btn btn-outline" @click="reset">Reset</button>
            <button class="btn btn-primary" :disabled="saving || loading" @click="save">
              <span v-if="saving">Saving…</span>
              <span v-else>Save changes</span>
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import propertiesApi from '@/services/properties.js'
import ownerPortal from '@/services/ownerPortal.js'
import SETTINGS from '@/config/settings.js'
import ComplianceItem from '@/components/properties/ComplianceItem.vue'

const emptyForm = () => ({
  full_legal_name: '',
  primary_email: '',
  phone_number: '',
  ownership_type: '',
  date_of_birth: '',
  address_line_1: '',
  address_line_2: '',
  city: '',
  postal_code: '',
  country: 'GB',
  utr_number: '',
  national_insurance_number: '',
  dac7_status: 'details_needed',
  identity_document: null,
  identity_document_current: '',
})

const emptyDetails = () => ({
  address_line_1: '',
  address_line_2: '',
  city: '',
  postal_code: '',
  country: '',
})

const emptySettings = () => ({
  management_fee_rate: 16,
  management_fee_base: 'net',
  cleaning_mode: 'rentlio',
  cleaning_fee: 0,
  cleaning_per_booking: 150,
  cleaning_vat_applicable: false,
  cleaning_enabled: true,
  laundry_calculation_mode: 'fixed',
  laundry_fee: 0,
  laundry_per_booking: 15,
  laundry_vat_applicable: true,
  laundry_enabled: true,
  consumables: 0,
  consumables_enabled: true,
  airbnb_commission_rate: '',
  booking_com_commission_rate: '',
  other_platform_commission_rate: '',
  // Scottish Visitor Levy (and other local pass-through tourism taxes).
  visitor_levy_enabled: false,
  visitor_levy_method: 'pct',
  visitor_levy_rate: 5,
  visitor_levy_night_cap: 5,
  visitor_levy_description: '',
  visitor_levy_council_name: '',
  visitor_levy_live_date: '',
  visitor_levy_show_on_statement: true,
  visitor_levy_xero_liability: true,
  visitor_levy_vat_rate: 20,
  visitor_levy_bundled_extras_excluded: true,
  visitor_levy_provider_retention_pct: 0,
})

const COUNTRY_LABELS = {
  GB: 'United Kingdom',
  US: 'United States',
  IE: 'Ireland',
  DE: 'Germany',
  FR: 'France',
  OTHER: 'Other',
}

const OWNERSHIP_LABELS = {
  personal: 'Individual',
  company: 'Limited company',
  trust: 'Trust',
}

export default {
  name: 'Dac7EditDrawer',
  components: { ComplianceItem },
  props: {
    property: { type: Object, required: true },
  },
  data() {
    return {
      tab: 'owner',
      form: emptyForm(),
      initial: emptyForm(),
      loading: false,
      saving: false,
      exists: false,
      error: '',
      addedDate: '',
      editingField: null,
      settings: emptySettings(),
      initialSettings: emptySettings(),
      settingsLoading: false,
      savingSettings: false,
      settingsLoaded: false,
      settingsError: '',
      globalOtaRates: { airbnb: 18.6, booking: 15, other: 10 },
      details: emptyDetails(),
      initialDetails: emptyDetails(),
      loadingDetails: false,
      savingDetails: false,
      detailsLoaded: false,
      detailsError: '',
      ownerId: null,
      linkStatus: { state: 'never_sent', sent_to_email: '', expires_at: '', created_at: '', used_at: null },
      sendingLink: false,
      linkSendError: '',
    }
  },
  computed: {
    ownerDetailsComplete() {
      return !!(this.form.full_legal_name && this.form.primary_email)
    },
    dac7DetailsComplete() {
      const addr = this.form.address_line_1 && this.form.city && this.form.postal_code
      return !!(this.form.full_legal_name && addr && this.form.country && this.form.ownership_type)
    },
    isVerified() {
      return this.ownerDetailsComplete && this.dac7DetailsComplete
    },
    ownerDetailsPillClass() { return this.ownerDetailsComplete ? 'pill-green' : 'pill-amber' },
    ownerDetailsPillLabel() {
      if (this.ownerDetailsComplete) return 'Complete'
      const missing = [!this.form.full_legal_name, !this.form.primary_email].filter(Boolean).length + 1 // +1 for bank future
      return `${missing} item${missing > 1 ? 's' : ''} pending`
    },
    dac7DetailsPillClass() { return this.dac7DetailsComplete ? 'pill-green' : 'pill-amber' },
    dac7DetailsPillLabel() {
      if (this.dac7DetailsComplete) return 'Complete'
      return 'Awaiting details'
    },
    commissionLabel() {
      return this.property.commission != null ? this.property.commission + '%' : '—'
    },
    ownerStatusLabel() {
      if (this.isVerified) return 'Verified ✓'
      if (!this.form.primary_email) return 'Not set'
      return 'Pending'
    },
    ownerColor() {
      if (this.isVerified) return '#a3f7bf'
      if (!this.form.primary_email) return 'rgba(255,255,255,0.6)'
      return '#fdcb6e'
    },
    dac7StatusLabel() {
      if (this.dac7DetailsComplete) return 'Complete ✓'
      if (this.form.address_line_1 || this.form.date_of_birth || this.form.utr_number) return 'Incomplete'
      return 'Details needed'
    },
    dac7Color() {
      if (this.dac7DetailsComplete) return '#a3f7bf'
      if (this.form.address_line_1 || this.form.date_of_birth || this.form.utr_number) return '#fdcb6e'
      return 'rgba(255,255,255,0.6)'
    },
    ownerPillClass() {
      if (this.isVerified) return 'pill-green'
      if (!this.form.primary_email) return 'pill-grey'
      return 'pill-amber'
    },
    ownerPillLabel() {
      if (this.isVerified) return 'Verified ✓'
      if (!this.form.primary_email) return 'Not set'
      return 'Pending setup'
    },
    ownershipTypeLabel() {
      return OWNERSHIP_LABELS[this.form.ownership_type] || '—'
    },
    countryLabel() {
      return COUNTRY_LABELS[this.form.country] || this.form.country || ''
    },
    addressDisplay() {
      if (!this.form.address_line_1) return ''
      const parts = [this.form.address_line_1, this.form.city, this.form.postal_code].filter(Boolean)
      return parts.join(', ')
    },
    dobDisplay() {
      if (!this.form.date_of_birth) return ''
      try {
        return new Date(this.form.date_of_birth).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
      } catch (e) { return this.form.date_of_birth }
    },
    taxRefDisplay() {
      const parts = []
      if (this.form.utr_number) parts.push(`UTR: ${this.form.utr_number}`)
      if (this.form.national_insurance_number) parts.push(`NI: ${this.form.national_insurance_number}`)
      return parts.join(' · ')
    },
    identityDisplay() {
      if (this.form.identity_document instanceof File) return this.form.identity_document.name
      if (this.form.identity_document_current) return 'Uploaded'
      return ''
    },
    computedStatus() {
      const done = [this.ownerDetailsComplete, this.dac7DetailsComplete].filter(Boolean).length
      if (done === 2) return 'complete'
      if (done === 0) return 'details_needed'
      return 'incomplete'
    },
    cleaningBaseAmount() {
      if (this.settings.cleaning_mode === 'monthly') return parseFloat(this.settings.cleaning_fee) || 0
      if (this.settings.cleaning_mode === 'fixed' || this.settings.cleaning_mode === 'per_booking') return parseFloat(this.settings.cleaning_per_booking) || 0
      return 0
    },
    cleaningTotalLabel() {
      const base = this.cleaningBaseAmount
      if (this.settings.cleaning_vat_applicable) return `£${base.toFixed(2)} + VAT (£${(base * 1.2).toFixed(2)})`
      return `£${base.toFixed(2)} — no VAT`
    },
    cleaningTotalPillClass() {
      return this.settings.cleaning_vat_applicable ? 'total-pill green' : 'total-pill grey'
    },
    laundryBaseAmount() {
      if (this.settings.laundry_calculation_mode === 'fixed') return parseFloat(this.settings.laundry_fee) || 0
      return parseFloat(this.settings.laundry_per_booking) || 0
    },
    laundryTotalLabel() {
      const base = this.laundryBaseAmount
      if (this.settings.laundry_vat_applicable) return `£${base.toFixed(2)} + VAT (£${(base * 1.2).toFixed(2)})`
      return `£${base.toFixed(2)} — no VAT`
    },
    laundryTotalPillClass() {
      return this.settings.laundry_vat_applicable ? 'total-pill green' : 'total-pill grey'
    },
    consumablesTotalLabel() {
      const base = parseFloat(this.settings.consumables) || 0
      return `£${base.toFixed(2)} + VAT = £${(base * 1.2).toFixed(2)}`
    },
    airbnbGlobalPlaceholder() { return `${this.globalOtaRates.airbnb.toFixed(2)} (global)` },
    bookingGlobalPlaceholder() { return `${this.globalOtaRates.booking.toFixed(2)} (global)` },
    otherGlobalPlaceholder() { return `${this.globalOtaRates.other.toFixed(2)} (global)` },
    // The night cap only makes sense for methods that scale with nights
    // (pct + night). Fixed-per-booking and fixed-per-guest ignore it.
    levyCapApplicable() {
      return this.settings.visitor_levy_method === 'pct' || this.settings.visitor_levy_method === 'night'
    },
    canSendSetupLink() {
      return !!this.form.primary_email && /.+@.+\..+/.test(this.form.primary_email)
    },
    linkPillClass() {
      const s = this.linkStatus.state
      if (s === 'pending') return 'pill-blue'
      if (s === 'used') return 'pill-green'
      if (s === 'expired') return 'pill-amber'
      return 'pill-amber'
    },
    linkPillLabel() {
      const s = this.linkStatus.state
      if (s === 'pending') return 'Awaiting owner'
      if (s === 'used') return 'Owner replied'
      if (s === 'expired') return 'Link expired'
      return 'Not yet set up'
    },
    linkStatusMessage() {
      const s = this.linkStatus.state
      if (s === 'pending') {
        const expiresAbs = this.linkStatus.expires_at
          ? new Date(this.linkStatus.expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
          : ''
        const to = this.linkStatus.sent_to_email || this.form.primary_email
        return `Setup link sent to ${to}. Expires on ${expiresAbs}.`
      }
      if (s === 'used') {
        return 'The owner has completed the form. Their details are now on file.'
      }
      if (s === 'expired') {
        return 'The previous link has expired. Send a new one below.'
      }
      return 'No setup link sent yet. Send the owner a setup link to collect their details.'
    },
  },
  async mounted() {
    await this.load()
  },
  methods: {
    initials(name) {
      if (!name) return '·'
      return name.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase()
    },
    async load() {
      this.loading = true
      this.error = ''
      try {
        const res = await propertiesApi.getOwner(this.property.id)
        const data = res && res.data
        if (data) {
          this.exists = true
          this.ownerId = data.id || null
          const loaded = {
            ...emptyForm(),
            ...data,
            date_of_birth: data.date_of_birth || '',
            identity_document: null,
            identity_document_current: data.identity_document || '',
          }
          if (loaded.identity_document_current && !/^https?:\/\//.test(loaded.identity_document_current)) {
            const base = SETTINGS.API.baseUrl.replace(/\/dev$/, '')
            loaded.identity_document_current = base + (loaded.identity_document_current.startsWith('/') ? '' : '/') + loaded.identity_document_current
          }
          this.form = loaded
          this.initial = { ...loaded }
          this.addedDate = data.created_at ? new Date(data.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''
          if (this.ownerId) this.fetchLinkStatus()
        } else {
          this.exists = false
          this.ownerId = null
          const empty = emptyForm()
          empty.full_legal_name = this.property.owner_name || ''
          empty.primary_email = this.property.owner_email || ''
          this.form = empty
          this.initial = { ...empty }
          this.linkStatus = { state: 'never_sent', sent_to_email: '', expires_at: '', created_at: '', used_at: null }
        }
      } catch (e) {
        this.error = 'Failed to load owner details.'
      } finally {
        this.loading = false
      }
    },
    async fetchLinkStatus() {
      if (!this.ownerId) return
      try {
        const res = await ownerPortal.linkStatus(this.ownerId)
        this.linkStatus = { ...this.linkStatus, ...res }
      } catch (e) {
        // Non-blocking: keep default never_sent state
      }
    },
    async sendSetupLink() {
      if (!this.ownerId || !this.canSendSetupLink) return
      this.sendingLink = true
      this.linkSendError = ''
      try {
        const res = await ownerPortal.sendSetupLink(this.ownerId, this.form.primary_email)
        this.linkStatus = {
          state: 'pending',
          sent_to_email: res.sent_to_email,
          expires_at: res.expires_at,
          created_at: res.created_at,
          used_at: null,
        }
        if (res.email_sent === false) {
          this.linkSendError = 'Link created but the email could not be delivered. Check the SendGrid config.'
        }
      } catch (e) {
        const detail = e && e.response && e.response.data && e.response.data.error
        this.linkSendError = detail || 'Failed to send setup link. Please retry.'
      } finally {
        this.sendingLink = false
      }
    },
    reset() {
      this.form = { ...this.initial }
      this.editingField = null
      this.error = ''
    },
    formatApiError(e, fallback = 'Something went wrong. Please try again.') {
      const data = e && e.response && e.response.data
      if (!data) {
        return e && e.message ? 'Network error. Please check your connection.' : fallback
      }
      if (data.message) return data.message
      if (data.detail && typeof data.detail === 'string') return data.detail
      if (data.errors && typeof data.errors === 'object') {
        const parts = []
        for (const [field, msgs] of Object.entries(data.errors)) {
          const label = field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
          const msg = Array.isArray(msgs) ? msgs.join(' ') : String(msgs)
          parts.push(`${label}: ${msg}`)
        }
        if (parts.length) return parts.join(' — ')
      }
      const status = e && e.response && e.response.status
      if (status === 401 || status === 403) return 'You are not authorized. Please sign in again.'
      if (status === 404) return 'Not found. The item may have been deleted.'
      if (status >= 500) return 'Server error. Please try again in a moment.'
      return fallback
    },
    onFileChange(file) {
      this.form.identity_document = file
      this.editingField = null
    },
    onFieldSave() {
      this.editingField = null
    },
    async save() {
      if (!this.form.primary_email) {
        this.error = 'Primary email is required.'
        return
      }
      this.saving = true
      this.error = ''
      try {
        this.form.dac7_status = this.computedStatus
        const payload = { ...this.form }
        delete payload.identity_document_current
        const hasFile = !!(payload.identity_document instanceof File)
        if (!hasFile) delete payload.identity_document
        if (!payload.date_of_birth) delete payload.date_of_birth

        if (this.exists) {
          await propertiesApi.saveOwner(this.property.id, payload, hasFile)
        } else {
          const createPayload = { ...payload }
          delete createPayload.identity_document
          await propertiesApi.createOwner(this.property.id, createPayload)
          if (hasFile) {
            await propertiesApi.saveOwner(this.property.id, { identity_document: this.form.identity_document }, true)
          }
        }
        this.$emit('saved')
      } catch (e) {
        this.error = this.formatApiError(e, 'Could not save the owner. Please try again.')
      } finally {
        this.saving = false
      }
    },
    close() {
      this.$emit('close')
    },
    async onTabChange(next) {
      this.tab = next
      if (next === 'financial' && !this.settingsLoaded) {
        await this.loadSettings()
      }
      if (next === 'details' && !this.detailsLoaded) {
        await this.loadDetails()
      }
    },
    async loadDetails() {
      this.loadingDetails = true
      this.detailsError = ''
      try {
        const res = await propertiesApi.getDetails(this.property.id)
        const data = res && res.data
        const loaded = { ...emptyDetails(), ...(data || {}) }
        Object.keys(emptyDetails()).forEach((k) => {
          if (loaded[k] === null || loaded[k] === undefined) loaded[k] = ''
        })
        this.details = loaded
        this.initialDetails = { ...loaded }
        this.detailsLoaded = true
      } catch (e) {
        this.detailsError = 'Failed to load property details.'
      } finally {
        this.loadingDetails = false
      }
    },
    resetDetails() {
      this.details = { ...this.initialDetails }
      this.detailsError = ''
    },
    pmsFallbackPlaceholder(key, label) {
      const pmsKey = 'pms_' + key
      const pmsVal = this.property[pmsKey]
      if (pmsVal) return `${pmsVal} (from PMS)`
      return `${label} (not set in PMS)`
    },
    async saveDetails() {
      this.savingDetails = true
      this.detailsError = ''
      try {
        const payload = { ...this.details }
        await propertiesApi.saveDetails(this.property.id, payload)
        this.initialDetails = { ...this.details }
        this.$emit('saved')
      } catch (e) {
        this.detailsError = this.formatApiError(e, 'Could not save property details. Please try again.')
      } finally {
        this.savingDetails = false
      }
    },
    async loadSettings() {
      this.settingsLoading = true
      this.settingsError = ''
      try {
        const res = await propertiesApi.getSettings(this.property.id)
        const data = res && res.data
        if (data) {
          const normalized = { ...emptySettings(), ...data }
          Object.keys(normalized).forEach((k) => {
            const v = normalized[k]
            if (typeof v === 'string' && v !== '' && !isNaN(parseFloat(v))) {
              const asNum = parseFloat(v)
              if ([
                'management_fee_rate', 'cleaning_fee', 'cleaning_per_booking',
                'laundry_fee', 'laundry_per_booking', 'consumables',
                'airbnb_commission_rate', 'booking_com_commission_rate',
                'other_platform_commission_rate',
                'visitor_levy_rate', 'visitor_levy_vat_rate',
                'visitor_levy_provider_retention_pct',
              ].includes(k)) {
                normalized[k] = asNum
              }
            }
          })
          // Backend now exposes a persisted `consumables_enabled` flag. If a
          // legacy record is missing the field, default it based on whether an
          // amount was configured (>0) so existing behaviour is preserved.
          if (typeof normalized.consumables_enabled !== 'boolean') {
            normalized.consumables_enabled = parseFloat(normalized.consumables) > 0
          }
          if (typeof normalized.cleaning_enabled !== 'boolean') {
            normalized.cleaning_enabled = true
          }
          if (typeof normalized.laundry_enabled !== 'boolean') {
            normalized.laundry_enabled = true
          }
          this.settings = normalized
          this.initialSettings = { ...normalized }
        }
        this.settingsLoaded = true
      } catch (e) {
        this.settingsError = 'Failed to load financial settings.'
      } finally {
        this.settingsLoading = false
      }
    },
    resetSettings() {
      this.settings = { ...this.initialSettings }
      this.settingsError = ''
    },
    async saveSettings() {
      this.savingSettings = true
      this.settingsError = ''
      try {
        const payload = { ...this.settings, property_id: this.property.id }
        // Legacy PropertySettings.maintenance retired — never round-trip it back
        // to the backend, otherwise a save re-persists the historical value.
        delete payload.maintenance
        // Empty string / NaN for OTA overrides → don't send (use global)
        ;['airbnb_commission_rate', 'booking_com_commission_rate', 'other_platform_commission_rate'].forEach((k) => {
          const v = payload[k]
          if (v === '' || v === null || (typeof v === 'number' && isNaN(v))) delete payload[k]
        })
        // Persist the toggles explicitly and zero the amount when disabled so
        // the backend sees a consistent state.
        payload.consumables_enabled = !!this.settings.consumables_enabled
        if (!payload.consumables_enabled) payload.consumables = 0
        payload.cleaning_enabled = !!this.settings.cleaning_enabled
        payload.laundry_enabled = !!this.settings.laundry_enabled
        // Visitor levy — coerce the optional fields to their expected types
        // so the DRF PositiveIntegerField / DateField don't reject blanks.
        payload.visitor_levy_enabled = !!this.settings.visitor_levy_enabled
        const capRaw = this.settings.visitor_levy_night_cap
        payload.visitor_levy_night_cap = (capRaw === '' || capRaw === null || capRaw === undefined || Number.isNaN(Number(capRaw)))
          ? null
          : Number(capRaw)
        if (!payload.visitor_levy_live_date) payload.visitor_levy_live_date = null
        await propertiesApi.saveSettings(this.property.id, payload)
        this.initialSettings = { ...this.settings }
        this.$emit('saved')
      } catch (e) {
        this.settingsError = this.formatApiError(e, 'Could not save financial settings. Please try again.')
      } finally {
        this.savingSettings = false
      }
    },
  },
}
</script>

<style scoped>
.drawer-overlay {
  position: fixed; inset: 0; background: rgba(26,24,61,0.45);
  z-index: 700; display: flex; justify-content: flex-end;
  --sidebar-grad: linear-gradient(180deg, #4a38b5 0%, #3d2f9e 100%);
  --accent: #5b4fcf;
  --accent-dim: rgba(91,79,207,0.08);
  --accent-border: rgba(91,79,207,0.25);
  --bg: #f5f4fb;
  --white: #ffffff;
  --text: #1a1a2e;
  --text-mid: #4a4880;
  --text-muted: #9896bb;
  --border: #e8e6f4;
  --green: #00b894;
  --green-dim: rgba(0,184,148,0.1);
  --green-border: rgba(0,184,148,0.25);
  --amber: #fdcb6e;
  --amber-text: #b7791f;
  --amber-dim: rgba(253,203,110,0.2);
  --amber-border: rgba(253,203,110,0.5);
  --red: #d63031;
  --red-dim: rgba(214,48,49,0.08);
  --red-border: rgba(214,48,49,0.2);
  --gold: #f59e0b;
  --gold-dim: rgba(245,158,11,0.1);
  --gold-border: rgba(245,158,11,0.3);
  --shadow-sm: 0 1px 4px rgba(26,24,61,0.07);
  --radius: 10px;
  font-family: 'Inter', sans-serif;
}
.drawer { width: 720px; max-width: 100%; background: var(--bg); height: 100%; display: flex; flex-direction: column; box-shadow: -8px 0 24px rgba(26,24,61,0.15); }
.drawer *, .drawer *::before, .drawer *::after { box-sizing: border-box; }

.drawer-topbar { background: var(--white); border-bottom: 1px solid var(--border); padding: 0 22px; height: 52px; display: flex; align-items: center; justify-content: space-between; box-shadow: var(--shadow-sm); }
.drawer-topbar-crumb { display: flex; align-items: center; gap: 7px; font-size: 13px; color: var(--text-muted); }
.drawer-topbar-crumb svg { width: 13px; height: 13px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; }
.crumb { cursor: pointer; transition: color 0.12s; }
.crumb:hover { color: var(--accent); }
.crumb-current { font-family: 'Outfit', sans-serif; font-size: 1rem; font-weight: 700; color: var(--text); }
.btn-close { background: transparent; border: none; padding: 6px; cursor: pointer; border-radius: 6px; }
.btn-close:hover { background: var(--bg); }
.btn-close svg { width: 18px; height: 18px; fill: none; stroke: var(--text-mid); stroke-width: 2; stroke-linecap: round; }

.drawer-body { padding: 22px 24px; overflow-y: auto; flex: 1; }

.prop-hero { background: var(--sidebar-grad); border-radius: var(--radius); padding: 22px 26px; margin-bottom: 22px; display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.prop-hero-left { display: flex; align-items: center; gap: 14px; }
.prop-avatar-lg { width: 48px; height: 48px; border-radius: 11px; background: rgba(255,255,255,0.15); display: flex; align-items: center; justify-content: center; font-family: 'Outfit', sans-serif; font-size: 15px; font-weight: 800; color: #fff; flex-shrink: 0; }
.prop-hero-name { font-family: 'Outfit', sans-serif; font-size: 1.3rem; font-weight: 800; color: #fff; letter-spacing: -0.02em; margin-bottom: 3px; }
.prop-hero-meta { font-size: 12px; color: rgba(255,255,255,0.55); }
.prop-hero-stats { display: flex; gap: 24px; }
.prop-hero-stat-label { font-size: 10px; color: rgba(255,255,255,0.4); text-transform: uppercase; letter-spacing: 0.07em; margin-bottom: 2px; }
.prop-hero-stat-value { font-family: 'Outfit', sans-serif; font-size: 1rem; font-weight: 700; color: rgba(255,255,255,0.9); }

.settings-tabs { display: flex; gap: 2px; border-bottom: 1px solid var(--border); margin-bottom: 20px; }
.settings-tab { padding: 8px 16px; font-size: 13px; font-weight: 600; color: var(--text-muted); cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -1px; transition: color 0.15s, border-color 0.15s; white-space: nowrap; display: flex; align-items: center; gap: 6px; }
.settings-tab:hover { color: var(--text-mid); }
.settings-tab.active { color: var(--accent); border-bottom-color: var(--accent); }

.owner-assignment { background: var(--white); border-radius: var(--radius); border: 1.5px solid var(--accent-border); box-shadow: var(--shadow-sm); margin-bottom: 14px; overflow: hidden; }
.owner-assignment-header { padding: 14px 22px; border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; background: var(--accent-dim); }
.owner-mini-avatar { width: 38px; height: 38px; border-radius: 9px; background: var(--accent-dim); display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: var(--accent); }
.owner-verified-block { display: flex; align-items: center; gap: 14px; padding: 18px 22px; }
.owner-avatar { width: 42px; height: 42px; border-radius: 10px; background: var(--accent-dim); display: flex; align-items: center; justify-content: center; font-family: 'Outfit', sans-serif; font-size: 13px; font-weight: 800; color: var(--accent); flex-shrink: 0; }
.invite-row { display: flex; align-items: flex-end; gap: 10px; margin-bottom: 10px; }
.invite-status-row { display: flex; align-items: center; gap: 10px; padding: 10px 14px; border-radius: 8px; font-size: 12px; }
.invite-status-row svg { width: 14px; height: 14px; fill: none; stroke: currentColor; stroke-width: 2; flex-shrink: 0; }
.invite-status-row.pending { background: var(--amber-dim); border: 1px solid var(--amber-border); color: var(--amber-text); }
.invite-status-row.ok { background: #eeedfe; border: 1px solid #c4c0f0; color: #3c3489; }
.invite-status-row.error { background: #fdecea; border: 1px solid #f5b5b3; color: #7a1f1e; }
.pill-blue { background: #eeedfe; color: #3c3489; border: 1px solid #c4c0f0; }

.card { background: var(--white); border-radius: var(--radius); border: 1px solid var(--border); box-shadow: var(--shadow-sm); overflow: hidden; margin-bottom: 14px; }
.card.future-card { opacity: 0.7; }
.card-header { padding: 14px 18px; border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.card-title { font-family: 'Outfit', sans-serif; font-size: 0.9rem; font-weight: 700; color: var(--text); display: flex; align-items: center; gap: 8px; }
.card-title svg { width: 15px; height: 15px; fill: none; stroke: var(--accent); stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.card-subtitle { font-size: 12px; color: var(--text-muted); margin-top: 2px; }
.card-body { padding: 16px 18px; }

.field-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.field { display: flex; flex-direction: column; gap: 5px; }
.field.span2 { grid-column: 1/-1; }
label.field-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; color: var(--text-muted); }
input[type=text], input[type=email], input[type=tel], input[type=date], input[type=number], select {
  border: 1.5px solid var(--border); border-radius: 8px; padding: 9px 12px; font-size: 13px; color: var(--text);
  font-family: 'Inter', sans-serif; background: var(--white); outline: none; width: 100%;
  transition: border-color 0.15s, box-shadow 0.15s;
}
input[readonly], input[disabled], select[disabled] { background: var(--bg); color: var(--text-muted); cursor: default; }
input:focus, select:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(91,79,207,0.1); }
input::placeholder { color: var(--text-muted); }
select { -webkit-appearance: none; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%239896bb'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 12px center; background-size: 8px; padding-right: 30px; }

.radio-group { display: flex; flex-direction: column; gap: 8px; }
.radio-opt { display: flex; align-items: flex-start; gap: 10px; padding: 10px 13px; border: 1.5px solid var(--border); border-radius: 9px; }
.radio-opt.active { border-color: var(--accent); background: var(--accent-dim); }
.radio-dot-wrap { width: 16px; height: 16px; border-radius: 50%; border: 2px solid var(--border); flex-shrink: 0; display: flex; align-items: center; justify-content: center; margin-top: 1px; }
.radio-opt.active .radio-dot-wrap { border-color: var(--accent); background: var(--accent); }
.radio-opt.active .radio-dot-wrap::after { content: ''; width: 6px; height: 6px; border-radius: 50%; background: #fff; }
.radio-opt-text strong { display: block; font-size: 13px; font-weight: 600; color: var(--text); margin-bottom: 2px; }
.radio-opt.active .radio-opt-text strong { color: var(--accent); }
.radio-opt-text span { font-size: 11px; color: var(--text-muted); }

.pill { display: inline-flex; align-items: center; gap: 4px; padding: 3px 9px; border-radius: 20px; font-size: 11px; font-weight: 600; white-space: nowrap; }
.pill::before { content: ''; width: 5px; height: 5px; border-radius: 50%; background: currentColor; opacity: 0.7; }
.pill-green { background: var(--green-dim); color: var(--green); border: 1px solid var(--green-border); }
.pill-amber { background: var(--amber-dim); color: var(--amber-text); border: 1px solid var(--amber-border); }
.pill-grey { background: var(--bg); color: var(--text-muted); border: 1px solid var(--border); }

.btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; border: none; font-family: 'Inter', sans-serif; transition: opacity 0.15s; }
.btn:hover:not(:disabled) { opacity: 0.88; }
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn-primary { background: var(--accent); color: #fff; }
.btn-outline { background: transparent; color: var(--text-mid); border: 1.5px solid var(--border); }
.btn-sm { padding: 5px 11px; font-size: 12px; }
.btn svg { width: 13px; height: 13px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; }
.btn-row-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 18px; padding-top: 14px; border-top: 1px solid var(--border); }

.input-prefix { display: flex; align-items: center; }
.input-prefix span { background: var(--bg); border: 1.5px solid var(--border); border-right: none; border-radius: 8px 0 0 8px; padding: 9px 10px; font-size: 13px; color: var(--text-muted); }
.input-prefix input { border-radius: 0 8px 8px 0; }

.vat-badge { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 600; padding: 3px 9px; border-radius: 20px; }
.vat-badge.on { background: var(--green-dim); color: var(--green); border: 1px solid var(--green-border); }
.vat-badge.off { background: var(--bg); color: var(--text-muted); border: 1px solid var(--border); }

.toggle-row { display: flex; align-items: center; justify-content: space-between; padding: 11px 0; border-bottom: 1px solid #f0eef8; gap: 16px; }
.toggle-row:last-child { border-bottom: none; }
.toggle-info strong { display: block; font-size: 13px; font-weight: 600; color: var(--text); margin-bottom: 2px; }
.toggle-info span { font-size: 11px; color: var(--text-muted); }
.toggle { position: relative; width: 36px; height: 20px; flex-shrink: 0; }
.toggle input { opacity: 0; width: 0; height: 0; position: absolute; }
.toggle-track { position: absolute; inset: 0; background: var(--border); border-radius: 10px; cursor: pointer; transition: background 0.2s; }
.toggle input:checked + .toggle-track { background: var(--accent); }
.toggle-thumb { position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; background: #fff; border-radius: 50%; transition: transform 0.2s; pointer-events: none; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }
.toggle input:checked ~ .toggle-thumb { transform: translateX(16px); }

.radio-opt { cursor: pointer; transition: border-color 0.15s, background 0.15s; }
.radio-opt:hover { border-color: var(--accent); background: var(--accent-dim); }

.total-pill { display: inline-flex; align-items: center; gap: 6px; padding: 7px 12px; border-radius: 8px; font-size: 12px; font-weight: 600; white-space: nowrap; }
.total-pill.green { background: var(--green-dim); color: var(--green); border: 1px solid var(--green-border); }
.total-pill.grey { background: var(--bg); color: var(--text-mid); border: 1px solid var(--border); }

.vat-note { display: inline-flex; align-items: center; gap: 6px; padding: 7px 11px; border-radius: 8px; background: var(--green-dim); border: 1px solid var(--green-border); font-size: 12px; font-weight: 600; color: var(--green); white-space: nowrap; }
.vat-note svg { width: 13px; height: 13px; fill: none; stroke: var(--green); stroke-width: 2.5; stroke-linecap: round; }

.consumables-pill { display: inline-flex; align-items: center; gap: 6px; padding: 7px 12px; border-radius: 8px; background: var(--accent-dim); border: 1px solid var(--accent-border); font-size: 12px; font-weight: 600; color: var(--accent); white-space: nowrap; }

.method-opt { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 8px; border: 1px solid var(--border); cursor: pointer; font-size: 13px; color: var(--text); transition: border-color 0.15s, background 0.15s; }
.method-opt input[type="radio"] { margin: 0; accent-color: var(--accent); }
.method-opt.active { border-color: var(--accent); background: var(--accent-dim); font-weight: 500; }

.info-note { font-size: 11px; color: var(--text-muted); line-height: 1.5; padding: 8px 10px; background: var(--bg); border-radius: 8px; }

.future-tag { font-size: 9px; font-weight: 700; padding: 1px 6px; border-radius: 20px; background: var(--gold-dim); color: #92400e; border: 1px solid var(--gold-border); text-transform: uppercase; letter-spacing: 0.06em; margin-left: 4px; }

.info-box { display: flex; align-items: flex-start; gap: 10px; background: var(--accent-dim); border: 1px solid var(--accent-border); border-radius: 8px; padding: 11px 14px; font-size: 12px; color: var(--text-mid); line-height: 1.6; margin-bottom: 14px; }
.info-box svg { width: 14px; height: 14px; fill: none; stroke: var(--accent); stroke-width: 2; flex-shrink: 0; margin-top: 1px; }

.oblig-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.oblig-cell { padding: 12px 14px; background: var(--bg); border-radius: 8px; border: 1px solid var(--border); }
.oblig-cell.red { background: var(--red-dim); border-color: var(--red-border); }
.oblig-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em; color: var(--text-muted); margin-bottom: 6px; }
.oblig-value { font-family: 'Outfit', sans-serif; font-size: 1.1rem; font-weight: 800; color: var(--text); }
.oblig-sub { font-size: 11px; color: var(--text-muted); margin-top: 2px; }

.error-box { background: var(--red-dim); border: 1px solid var(--red-border); color: var(--red); border-radius: 8px; padding: 10px 12px; font-size: 12px; margin-bottom: 12px; }

.loading-state { text-align: center; padding: 40px 20px; color: var(--text-muted); font-size: 13px; }
.spinner { width: 26px; height: 26px; border: 3px solid var(--border); border-top-color: var(--accent); border-radius: 50%; margin: 0 auto 10px; animation: spin 0.9s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ── RESPONSIVE ── */
@media (max-width: 768px) {
  .drawer-overlay { padding: 0 !important; }
  .drawer {
    width: 100vw !important;
    max-width: 100vw !important;
    height: 100vh !important;
    box-shadow: none !important;
  }
  .drawer-topbar { padding: 0 14px; height: 52px; }
  .drawer-topbar-crumb { font-size: 12px; }
  .drawer-body { padding: 16px 14px; }
  .field-grid { grid-template-columns: 1fr !important; }
  /* Inline 3-col / 2-col grids in the drawer content */
  .drawer-body [style*="grid-template-columns:1fr 1fr 1fr"],
  .drawer-body [style*="grid-template-columns: 1fr 1fr 1fr"] {
    grid-template-columns: 1fr !important;
  }
  .drawer-body [style*="grid-template-columns:1fr 1fr"],
  .drawer-body [style*="grid-template-columns: 1fr 1fr"] {
    grid-template-columns: 1fr !important;
  }
  .prop-avatar-lg { width: 42px; height: 42px; font-size: 13px; }
  .owner-mini-avatar, .owner-avatar { width: 34px; height: 34px; font-size: 12px; }
  .card-title { font-size: 0.85rem; }
  .btn { padding: 8px 12px; font-size: 12px; }
  .btn-close { padding: 6px; }
  .btn-close svg { width: 20px; height: 20px; }
  .oblig-value { font-size: 1rem; }
}
</style>
